import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Boxes,
  ClipboardList,
  MapPin,
  MoreVertical,
  Package,
  PackagePlus,
  Pencil,
  Plus,
  Tags,
  Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INVENTORY_MANAGE_ROLES = ['admin', 'teamleader', 'hoofd_facilitair', 'connectleider', 'coordinator_worship'];

const ITEM_STATUSES = [
  { value: 'op_voorraad', label: 'Op voorraad', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'laag', label: 'Laag', className: 'bg-amber-100 text-amber-700' },
  { value: 'op', label: 'Op', className: 'bg-red-100 text-red-700' },
  { value: 'besteld', label: 'Besteld', className: 'bg-blue-100 text-blue-700' },
  { value: 'buiten_gebruik', label: 'Buiten gebruik', className: 'bg-slate-200 text-slate-600' },
];

const MUTATION_TYPES = [
  { value: 'aanvulling', label: 'Aanvulling (erbij)' },
  { value: 'gebruik', label: 'Gebruik (eraf)' },
  { value: 'correctie', label: 'Correctie (nieuw totaal)' },
  { value: 'afschrijving', label: 'Afschrijving (eraf)' },
  { value: 'retour', label: 'Retour (erbij)' },
];

const ORDER_REQUEST_STATUSES = [
  { value: 'aangevraagd', label: 'Aangevraagd', className: 'bg-amber-100 text-amber-700' },
  { value: 'goedgekeurd', label: 'Goedgekeurd', className: 'bg-blue-100 text-blue-700' },
  { value: 'besteld', label: 'Besteld', className: 'bg-indigo-100 text-indigo-700' },
  { value: 'ontvangen', label: 'Ontvangen', className: 'bg-emerald-100 text-emerald-700' },
  { value: 'afgewezen', label: 'Afgewezen', className: 'bg-red-100 text-red-700' },
];

const UNIT_OPTIONS = ['stuks', 'pak', 'liter', 'doos', 'rol'];

const EMPTY_ITEM_FORM = {
  name: '',
  category_id: '',
  description: '',
  unit: 'stuks',
  current_stock: '0',
  minimum_stock: '',
  desired_stock: '',
  storage_location_id: '',
  supplier_relation_id: '',
  order_code: '',
  price_indication: '',
  expiry_date: '',
  notes: '',
};

const EMPTY_CATEGORY_FORM = { name: '', description: '', responsible_role: '', default_storage_location_id: '' };
const EMPTY_LOCATION_FORM = { name: '', building_name: '', room_name: '', access_level: '', notes: '' };
const EMPTY_MUTATION_FORM = { mutation_type: 'aanvulling', quantity: '', reason: '', notes: '' };
const EMPTY_ORDER_REQUEST_FORM = { quantity: '', reason: '', desired_date: '', supplier_relation_id: '', cost_indication: '', notes: '' };

const getLabel = (options, value) => options.find((option) => option.value === value)?.label || value;
const getClassName = (options, value) => options.find((option) => option.value === value)?.className || 'bg-slate-100 text-slate-700';
const toNumberOrNull = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

const InventoryPage = () => {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('inventory_manage');

  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [orderRequests, setOrderRequests] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);

  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(EMPTY_CATEGORY_FORM);

  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState(EMPTY_LOCATION_FORM);

  const [mutationDialogItem, setMutationDialogItem] = useState(null);
  const [mutationForm, setMutationForm] = useState(EMPTY_MUTATION_FORM);

  const [orderRequestDialogItem, setOrderRequestDialogItem] = useState(null);
  const [orderRequestForm, setOrderRequestForm] = useState(EMPTY_ORDER_REQUEST_FORM);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [itemsRes, categoriesRes, locationsRes, ordersRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/inventory/items`),
        axios.get(`${API}/inventory/categories`),
        axios.get(`${API}/inventory/locations`),
        axios.get(`${API}/inventory/order-requests`),
        axios.get(`${API}/inventory/suppliers`),
      ]);
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
      setOrderRequests(ordersRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Kon voorraadgegevens niet laden');
    } finally {
      setLoading(false);
    }
  };

  const categoryName = (id) => categories.find((c) => c.id === id)?.name || '—';
  const locationName = (id) => locations.find((l) => l.id === id)?.name || '—';
  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || '—';
  const itemName = (id) => items.find((i) => i.id === id)?.name || '—';

  const stats = useMemo(() => ({
    total: items.length,
    low: items.filter((i) => i.status === 'laag').length,
    out: items.filter((i) => i.status === 'op').length,
    openOrders: orderRequests.filter((r) => ['aangevraagd', 'goedgekeurd', 'besteld'].includes(r.status)).length,
  }), [items, orderRequests]);

  // ---------- Item CRUD ----------
  const openCreateItem = () => {
    setEditingItem(null);
    setItemForm(EMPTY_ITEM_FORM);
    setItemDialogOpen(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name || '',
      category_id: item.category_id || '',
      description: item.description || '',
      unit: item.unit || 'stuks',
      current_stock: String(item.current_stock ?? 0),
      minimum_stock: item.minimum_stock ?? '' ? String(item.minimum_stock) : '',
      desired_stock: item.desired_stock ?? '' ? String(item.desired_stock) : '',
      storage_location_id: item.storage_location_id || '',
      supplier_relation_id: item.supplier_relation_id || '',
      order_code: item.order_code || '',
      price_indication: item.price_indication ?? '' ? String(item.price_indication) : '',
      expiry_date: item.expiry_date || '',
      notes: item.notes || '',
    });
    setItemDialogOpen(true);
  };

  const saveItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: itemForm.name,
        category_id: itemForm.category_id || null,
        description: itemForm.description || null,
        unit: itemForm.unit,
        storage_location_id: itemForm.storage_location_id || null,
        supplier_relation_id: itemForm.supplier_relation_id || null,
        order_code: itemForm.order_code || null,
        price_indication: toNumberOrNull(itemForm.price_indication),
        expiry_date: itemForm.expiry_date || null,
        minimum_stock: toNumberOrNull(itemForm.minimum_stock),
        desired_stock: toNumberOrNull(itemForm.desired_stock),
        notes: itemForm.notes || null,
      };

      if (editingItem) {
        await axios.put(`${API}/inventory/items/${editingItem.id}`, payload);
        toast.success('Artikel bijgewerkt');
      } else {
        await axios.post(`${API}/inventory/items`, { ...payload, current_stock: Number(itemForm.current_stock || 0) });
        toast.success('Artikel toegevoegd');
      }
      setItemDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Artikel "${item.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/inventory/items/${item.id}`);
      toast.success('Artikel verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Category CRUD ----------
  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      description: category.description || '',
      responsible_role: category.responsible_role || '',
      default_storage_location_id: category.default_storage_location_id || '',
    });
    setCategoryDialogOpen(true);
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: categoryForm.name,
        description: categoryForm.description || null,
        responsible_role: categoryForm.responsible_role || null,
        default_storage_location_id: categoryForm.default_storage_location_id || null,
      };
      if (editingCategory) {
        await axios.put(`${API}/inventory/categories/${editingCategory.id}`, payload);
        toast.success('Categorie bijgewerkt');
      } else {
        await axios.post(`${API}/inventory/categories`, payload);
        toast.success('Categorie toegevoegd');
      }
      setCategoryDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Categorie "${category.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/inventory/categories/${category.id}`);
      toast.success('Categorie verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Location CRUD ----------
  const openCreateLocation = () => {
    setEditingLocation(null);
    setLocationForm(EMPTY_LOCATION_FORM);
    setLocationDialogOpen(true);
  };

  const openEditLocation = (location) => {
    setEditingLocation(location);
    setLocationForm({
      name: location.name || '',
      building_name: location.building_name || '',
      room_name: location.room_name || '',
      access_level: location.access_level || '',
      notes: location.notes || '',
    });
    setLocationDialogOpen(true);
  };

  const saveLocation = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: locationForm.name,
        building_name: locationForm.building_name || null,
        room_name: locationForm.room_name || null,
        access_level: locationForm.access_level || null,
        notes: locationForm.notes || null,
      };
      if (editingLocation) {
        await axios.put(`${API}/inventory/locations/${editingLocation.id}`, payload);
        toast.success('Locatie bijgewerkt');
      } else {
        await axios.post(`${API}/inventory/locations`, payload);
        toast.success('Locatie toegevoegd');
      }
      setLocationDialogOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const deleteLocation = async (location) => {
    if (!window.confirm(`Locatie "${location.name}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/inventory/locations/${location.id}`);
      toast.success('Locatie verwijderd');
      fetchAll();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  // ---------- Mutations ----------
  const openMutationDialog = (item) => {
    setMutationDialogItem(item);
    setMutationForm(EMPTY_MUTATION_FORM);
  };

  const saveMutation = async (e) => {
    e.preventDefault();
    if (!mutationDialogItem) return;
    try {
      await axios.post(`${API}/inventory/mutations`, {
        item_id: mutationDialogItem.id,
        mutation_type: mutationForm.mutation_type,
        quantity: Number(mutationForm.quantity || 0),
        reason: mutationForm.reason || null,
        notes: mutationForm.notes || null,
      });
      toast.success('Voorraadmutatie geregistreerd');
      setMutationDialogItem(null);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Mutatie opslaan mislukt');
    }
  };

  // ---------- Order requests ----------
  const openOrderRequestDialog = (item) => {
    setOrderRequestDialogItem(item);
    setOrderRequestForm({
      ...EMPTY_ORDER_REQUEST_FORM,
      supplier_relation_id: item.supplier_relation_id || '',
    });
  };

  const saveOrderRequest = async (e) => {
    e.preventDefault();
    if (!orderRequestDialogItem) return;
    try {
      await axios.post(`${API}/inventory/order-requests`, {
        item_id: orderRequestDialogItem.id,
        quantity: Number(orderRequestForm.quantity || 0),
        reason: orderRequestForm.reason || null,
        desired_date: orderRequestForm.desired_date || null,
        supplier_relation_id: orderRequestForm.supplier_relation_id || null,
        cost_indication: toNumberOrNull(orderRequestForm.cost_indication),
        notes: orderRequestForm.notes || null,
      });
      toast.success('Bestelverzoek ingediend');
      setOrderRequestDialogItem(null);
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Bestelverzoek indienen mislukt');
    }
  };

  const updateOrderRequestStatus = async (request, status) => {
    try {
      await axios.put(`${API}/inventory/order-requests/${request.id}`, { status });
      toast.success('Status bijgewerkt');
      fetchAll();
    } catch (error) {
      toast.error('Status wijzigen mislukt');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Voorraadbeheer</h1>
            <p className="text-slate-500 text-sm">Artikelen, categorieën, locaties en bestelverzoeken</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Artikelen</p>
              <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Laag</p>
              <p className="text-2xl font-semibold text-amber-600">{stats.low}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Op</p>
              <p className="text-2xl font-semibold text-red-600">{stats.out}</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Openstaande bestelverzoeken</p>
              <p className="text-2xl font-semibold text-blue-600">{stats.openOrders}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList>
              <TabsTrigger value="items"><Package className="w-4 h-4 mr-2" />Artikelen</TabsTrigger>
              <TabsTrigger value="categories"><Tags className="w-4 h-4 mr-2" />Categorieën</TabsTrigger>
              <TabsTrigger value="locations"><MapPin className="w-4 h-4 mr-2" />Locaties</TabsTrigger>
              <TabsTrigger value="orders"><ClipboardList className="w-4 h-4 mr-2" />Bestelverzoeken</TabsTrigger>
            </TabsList>
          </div>

          {/* ARTIKELEN */}
          <TabsContent value="items" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateItem} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuw artikel
                </Button>
              </div>
            )}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 text-sm text-slate-500">Laden...</div>
                ) : items.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen artikelen toegevoegd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {items.map((item) => (
                      <div key={item.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{item.name}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(ITEM_STATUSES, item.status)}`}>
                              {getLabel(ITEM_STATUSES, item.status)}
                            </span>
                            {item.status === 'laag' || item.status === 'op' ? (
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            ) : null}
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            {categoryName(item.category_id)} · {item.current_stock} {item.unit}
                            {item.minimum_stock !== null && item.minimum_stock !== undefined ? ` (min. ${item.minimum_stock})` : ''}
                            {item.storage_location_id ? ` · ${locationName(item.storage_location_id)}` : ''}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openOrderRequestDialog(item)}>
                              <PackagePlus className="w-4 h-4 mr-2" />
                              Bestelverzoek indienen
                            </DropdownMenuItem>
                            {canManage && (
                              <>
                                <DropdownMenuItem onClick={() => openMutationDialog(item)}>
                                  <Boxes className="w-4 h-4 mr-2" />
                                  Voorraadmutatie
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditItem(item)}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Bewerken
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => deleteItem(item)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Verwijderen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CATEGORIEEN */}
          <TabsContent value="categories" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateCategory} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe categorie
                </Button>
              </div>
            )}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {categories.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen categorieën toegevoegd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {categories.map((category) => (
                      <div key={category.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900">{category.name}</div>
                          {category.description && <div className="text-sm text-slate-500">{category.description}</div>}
                        </div>
                        {canManage && (
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditCategory(category)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteCategory(category)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* LOCATIES */}
          <TabsContent value="locations" className="space-y-4">
            {canManage && (
              <div className="flex justify-end">
                <Button onClick={openCreateLocation} className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuwe locatie
                </Button>
              </div>
            )}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {locations.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen opslaglocaties toegevoegd.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {locations.map((location) => (
                      <div key={location.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900">{location.name}</div>
                          <div className="text-sm text-slate-500">
                            {[location.building_name, location.room_name].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </div>
                        {canManage && (
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditLocation(location)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteLocation(location)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BESTELVERZOEKEN */}
          <TabsContent value="orders" className="space-y-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-0">
                {orderRequests.length === 0 ? (
                  <div className="p-6 text-sm text-slate-500">Nog geen bestelverzoeken.</div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {orderRequests.map((request) => (
                      <div key={request.id} className="flex items-center gap-4 p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{itemName(request.item_id)}</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${getClassName(ORDER_REQUEST_STATUSES, request.status)}`}>
                              {getLabel(ORDER_REQUEST_STATUSES, request.status)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 mt-0.5">
                            Aantal: {request.quantity}
                            {request.reason ? ` · ${request.reason}` : ''}
                          </div>
                        </div>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {ORDER_REQUEST_STATUSES.map((option) => (
                                <DropdownMenuItem key={option.value} onClick={() => updateOrderRequestStatus(request, option.value)}>
                                  {option.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Item create/edit dialog */}
        <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Artikel bewerken' : 'Nieuw artikel'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveItem} className="space-y-4">
              <div>
                <Label htmlFor="item-name">Naam</Label>
                <Input id="item-name" required value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Categorie</Label>
                  <Select value={itemForm.category_id || undefined} onValueChange={(value) => setItemForm({ ...itemForm, category_id: value })}>
                    <SelectTrigger><SelectValue placeholder="Kies categorie" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Eenheid</Label>
                  <Select value={itemForm.unit} onValueChange={(value) => setItemForm({ ...itemForm, unit: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((unit) => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!editingItem && (
                <div>
                  <Label htmlFor="item-current-stock">Startvoorraad</Label>
                  <Input
                    id="item-current-stock"
                    type="number"
                    step="0.01"
                    value={itemForm.current_stock}
                    onChange={(e) => setItemForm({ ...itemForm, current_stock: e.target.value })}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="item-min-stock">Minimumvoorraad</Label>
                  <Input
                    id="item-min-stock"
                    type="number"
                    step="0.01"
                    value={itemForm.minimum_stock}
                    onChange={(e) => setItemForm({ ...itemForm, minimum_stock: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="item-desired-stock">Gewenste voorraad</Label>
                  <Input
                    id="item-desired-stock"
                    type="number"
                    step="0.01"
                    value={itemForm.desired_stock}
                    onChange={(e) => setItemForm({ ...itemForm, desired_stock: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Opslaglocatie</Label>
                <Select value={itemForm.storage_location_id || undefined} onValueChange={(value) => setItemForm({ ...itemForm, storage_location_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Leverancier</Label>
                <Select value={itemForm.supplier_relation_id || undefined} onValueChange={(value) => setItemForm({ ...itemForm, supplier_relation_id: value })}>
                  <SelectTrigger><SelectValue placeholder="Kies leverancier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Leveranciers beheer je bij Relaties (relatietype "Leverancier").
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="item-order-code">Artikelnummer/bestelcode</Label>
                  <Input id="item-order-code" value={itemForm.order_code} onChange={(e) => setItemForm({ ...itemForm, order_code: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="item-price">Prijsindicatie</Label>
                  <Input
                    id="item-price"
                    type="number"
                    step="0.01"
                    value={itemForm.price_indication}
                    onChange={(e) => setItemForm({ ...itemForm, price_indication: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="item-expiry">Houdbaarheidsdatum</Label>
                <Input
                  id="item-expiry"
                  type="date"
                  value={itemForm.expiry_date}
                  onChange={(e) => setItemForm({ ...itemForm, expiry_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="item-description">Omschrijving</Label>
                <Textarea id="item-description" value={itemForm.description} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="item-notes">Opmerkingen</Label>
                <Textarea id="item-notes" value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setItemDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  {editingItem ? 'Opslaan' : 'Artikel maken'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Category create/edit dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Categorie bewerken' : 'Nieuwe categorie'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveCategory} className="space-y-4">
              <div>
                <Label htmlFor="category-name">Naam</Label>
                <Input id="category-name" required value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="category-description">Omschrijving</Label>
                <Textarea id="category-description" value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="category-role">Verantwoordelijke rol of bediening</Label>
                <Input id="category-role" value={categoryForm.responsible_role} onChange={(e) => setCategoryForm({ ...categoryForm, responsible_role: e.target.value })} />
              </div>
              <div>
                <Label>Standaard opslaglocatie</Label>
                <Select
                  value={categoryForm.default_storage_location_id || undefined}
                  onValueChange={(value) => setCategoryForm({ ...categoryForm, default_storage_location_id: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                  <SelectContent>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setCategoryDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  {editingCategory ? 'Opslaan' : 'Categorie maken'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Location create/edit dialog */}
        <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingLocation ? 'Locatie bewerken' : 'Nieuwe locatie'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveLocation} className="space-y-4">
              <div>
                <Label htmlFor="location-name">Naam</Label>
                <Input id="location-name" required value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="location-building">Gebouw</Label>
                  <Input id="location-building" value={locationForm.building_name} onChange={(e) => setLocationForm({ ...locationForm, building_name: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="location-room">Ruimte</Label>
                  <Input id="location-room" value={locationForm.room_name} onChange={(e) => setLocationForm({ ...locationForm, room_name: e.target.value })} />
                </div>
              </div>
              <div>
                <Label htmlFor="location-access">Toegangsniveau</Label>
                <Input id="location-access" value={locationForm.access_level} onChange={(e) => setLocationForm({ ...locationForm, access_level: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="location-notes">Opmerkingen</Label>
                <Textarea id="location-notes" value={locationForm.notes} onChange={(e) => setLocationForm({ ...locationForm, notes: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setLocationDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  {editingLocation ? 'Opslaan' : 'Locatie maken'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Mutation dialog */}
        <Dialog open={!!mutationDialogItem} onOpenChange={(open) => { if (!open) setMutationDialogItem(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Voorraadmutatie — {mutationDialogItem?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveMutation} className="space-y-4">
              <div>
                <Label>Type mutatie</Label>
                <Select value={mutationForm.mutation_type} onValueChange={(value) => setMutationForm({ ...mutationForm, mutation_type: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MUTATION_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="mutation-quantity">
                  {mutationForm.mutation_type === 'correctie' ? 'Nieuw totaal' : 'Aantal'}
                </Label>
                <Input
                  id="mutation-quantity"
                  type="number"
                  step="0.01"
                  required
                  value={mutationForm.quantity}
                  onChange={(e) => setMutationForm({ ...mutationForm, quantity: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="mutation-reason">Reden</Label>
                <Input id="mutation-reason" value={mutationForm.reason} onChange={(e) => setMutationForm({ ...mutationForm, reason: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="mutation-notes">Opmerking</Label>
                <Textarea id="mutation-notes" value={mutationForm.notes} onChange={(e) => setMutationForm({ ...mutationForm, notes: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setMutationDialogItem(null)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  Mutatie opslaan
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Order request dialog */}
        <Dialog open={!!orderRequestDialogItem} onOpenChange={(open) => { if (!open) setOrderRequestDialogItem(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Bestelverzoek — {orderRequestDialogItem?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={saveOrderRequest} className="space-y-4">
              <div>
                <Label htmlFor="order-quantity">Aantal</Label>
                <Input
                  id="order-quantity"
                  type="number"
                  step="0.01"
                  required
                  value={orderRequestForm.quantity}
                  onChange={(e) => setOrderRequestForm({ ...orderRequestForm, quantity: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="order-reason">Reden</Label>
                <Input id="order-reason" value={orderRequestForm.reason} onChange={(e) => setOrderRequestForm({ ...orderRequestForm, reason: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="order-desired-date">Gewenste datum</Label>
                <Input
                  id="order-desired-date"
                  type="date"
                  value={orderRequestForm.desired_date}
                  onChange={(e) => setOrderRequestForm({ ...orderRequestForm, desired_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Leverancier</Label>
                <Select
                  value={orderRequestForm.supplier_relation_id || undefined}
                  onValueChange={(value) => setOrderRequestForm({ ...orderRequestForm, supplier_relation_id: value })}
                >
                  <SelectTrigger><SelectValue placeholder="Kies leverancier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>{supplier.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="order-cost">Kostenindicatie</Label>
                <Input
                  id="order-cost"
                  type="number"
                  step="0.01"
                  value={orderRequestForm.cost_indication}
                  onChange={(e) => setOrderRequestForm({ ...orderRequestForm, cost_indication: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="order-notes">Opmerking</Label>
                <Textarea id="order-notes" value={orderRequestForm.notes} onChange={(e) => setOrderRequestForm({ ...orderRequestForm, notes: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" className="rounded-xl" onClick={() => setOrderRequestDialogItem(null)}>
                  Annuleren
                </Button>
                <Button type="submit" className="rounded-xl bg-teal-600 hover:bg-teal-700">
                  Bestelverzoek indienen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default InventoryPage;
